import type { StorefrontBrand, StorefrontCatalog } from '@erp/shared';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { useStorefront } from '../../app/StorefrontLayout.js';
import { publicApi } from '../../lib/publicApi.js';

import { pricingRecorder } from './buyerPricing.js';
import { ProductCard } from './ProductCard.js';
import { ProductGridSkeleton } from './StorefrontSkeletons.js';
import { StoreImage } from './StoreImage.js';

const PAGE = 8;

/** A single brand's storefront page: brand header + its published products. */
export function StorefrontBrandPage(): JSX.Element {
  const { t, i18n } = useTranslation('cms');
  const { slug, site } = useStorefront();
  const { brandId = '' } = useParams();
  const [brand, setBrand] = useState<StorefrontBrand | null>(null);
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<StorefrontCatalog | null>(null);

  // Brand header comes from the brands list (no per-id endpoint needed).
  useEffect(() => {
    void publicApi().get<{ items: StorefrontBrand[] }>(`/${slug}/brands`).then((r) => setBrand(r.data.items.find((b) => b.id === brandId) ?? null)).catch(() => setBrand(null));
  }, [slug, brandId, i18n.language]);

  useEffect(() => {
    setData(null);
    const remember = pricingRecorder(slug);
    void publicApi().get<StorefrontCatalog>(`/${slug}/products`, { params: { brand: brandId, limit: PAGE, offset } })
      .then((r) => { setData(r.data); remember(r.data.pricing); })
      .catch(() => setData({ items: [], total: 0, categories: [] }));
  }, [slug, brandId, offset, i18n.language]);

  const title = brand?.name ?? t('store.brands.title', { defaultValue: 'Brands' });

  return (
    <div className="flex flex-col gap-6">
      <Helmet><title>{`${title} · ${site.storeName}`}</title></Helmet>

      <nav className="text-sm text-fg-muted">
        <Link to={`/s/${slug}/brands`} className="hover:text-fg">{t('store.brands.title', { defaultValue: 'Brands' })}</Link>
        <span className="px-2">/</span><span className="text-fg">{title}</span>
      </nav>

      <header className="flex items-center gap-4">
        <div className="grid h-16 w-16 flex-none place-items-center overflow-hidden rounded-xl border border-border bg-white p-2">
          <StoreImage src={brand?.imageUrl ?? null} alt={title} className="max-h-full max-w-full object-contain" />
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
          {brand && <p className="mt-1 text-sm text-fg-muted">{t('store.category.count', { count: brand.productCount })}</p>}
        </div>
      </header>

      {!data ? (
        <ProductGridSkeleton count={PAGE} />
      ) : data.items.length === 0 ? (
        <div className="py-16 text-center text-fg-muted">{t('store.shop.empty', { defaultValue: 'No products found.' })}</div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
            {data.items.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
          {data.total > PAGE && (() => {
            const pageCount = Math.ceil(data.total / PAGE);
            const current = Math.floor(offset / PAGE);
            const go = (p: number): void => setOffset(Math.min(Math.max(p, 0), pageCount - 1) * PAGE);
            const pill = (active: boolean): string =>
              `grid h-9 min-w-9 place-items-center rounded-full px-3 text-sm font-medium transition disabled:opacity-40 ${active ? 'bg-brand text-brand-contrast shadow-sm' : 'border border-border text-fg-muted hover:border-brand/40 hover:text-fg'}`;
            return (
              <nav className="flex items-center justify-center gap-2" aria-label={t('store.shop.pagination', { defaultValue: 'Pagination' })}>
                <button type="button" onClick={() => go(current - 1)} disabled={current === 0} className={pill(false)} aria-label={t('store.shop.prev', { defaultValue: 'Previous' })}>
                  <ChevronLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
                </button>
                <span className="px-2 text-sm text-fg-muted">{current + 1} / {pageCount}</span>
                <button type="button" onClick={() => go(current + 1)} disabled={current >= pageCount - 1} className={pill(false)} aria-label={t('store.shop.next', { defaultValue: 'Next' })}>
                  <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
                </button>
              </nav>
            );
          })()}
        </>
      )}
    </div>
  );
}
